import { io, Socket } from 'socket.io-client';
import { logger } from '../utils/logger';

// Validate required environment variables
if (!process.env.REACT_APP_API_URL) {
  logger.critical('REACT_APP_API_URL environment variable is not set!');
  throw new Error('Missing required environment variable: REACT_APP_API_URL');
}

const SOCKET_URL = process.env.REACT_APP_API_URL.replace('/api', '');

class SocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  connect(token: string) {
    if (this.socket?.connected) {
      logger.log('🔌 Socket already connected:', this.socket.id);
      return this.socket;
    }

    logger.log('🔌 Attempting to connect to Socket.IO server:', SOCKET_URL);
    logger.log('🔑 Using auth token (length):', token?.length || 0);

    this.socket = io(SOCKET_URL, {
      auth: {
        token,
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
      transports: ['websocket', 'polling'],
    });

    this.socket.on('connect', () => {
      logger.log('✅ Socket connected successfully! Socket ID:', this.socket?.id);
      this.reconnectAttempts = 0;
    });

    this.socket.on('disconnect', (reason) => {
      logger.log('❌ Socket disconnected. Reason:', reason);
    });

    this.socket.on('connect_error', (error) => {
      logger.error('❌ Socket connection error:', error.message);
      logger.error('Full error:', error);
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        logger.error('⛔ Max reconnection attempts reached. Please refresh the page.');
      }
    });

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      logger.log('Socket disconnected manually');
    }
  }

  subscribe(type: string, id: number) {
    if (this.socket?.connected) {
      this.socket.emit('subscribe', { type, id });
      logger.log(`📡 Subscribed to ${type}:${id}`);
    }
  }

  unsubscribe(type: string, id: number) {
    if (this.socket?.connected) {
      this.socket.emit('unsubscribe', { type, id });
      logger.log(`📴 Unsubscribed from ${type}:${id}`);
    }
  }

  on(event: string, callback: (data: any) => void) {
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  off(event: string, callback?: (data: any) => void) {
    if (this.socket) {
      if (callback) {
        this.socket.off(event, callback);
      } else {
        this.socket.off(event);
      }
    }
  }

  isConnected(): boolean {
    return !!this.socket?.connected;
  }

  getSocket(): Socket | null {
    return this.socket;
  }
}

export const socketService = new SocketService();
